import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { ZLogo } from "@/components/ZLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, ArrowLeft, Eye, EyeOff, Clock } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1579548122080-c35fd6820ecb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBuZW9uJTIwYmx1ZSUyMGdlb21ldHJ5fGVufDB8fHx8MTc4NTM4Nzc5NHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, verifyOtp, resendOtp, sessionMessage, clearSessionMessage } = useAuth();
  const [step, setStep] = useState("credentials"); // credentials | otp
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    clearSessionMessage();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res.twofa) {
        setStep("otp");
        setInfo("Abbiamo inviato un codice a 6 cifre alla tua email.");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Accesso non riuscito");
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyOtp(email, code.trim());
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Codice non valido");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setError("");
    try {
      await resendOtp(email);
      setInfo("Nuovo codice inviato alla tua email.");
    } catch {
      setError("Impossibile reinviare il codice. Riprova.");
    }
  };

  const backToCredentials = () => {
    setStep("credentials");
    setCode("");
    setError("");
    setInfo("");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F5F9FF]">
      <div className="relative hidden lg:block">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(11,107,203,0.55), rgba(9,44,86,0.94))" }} />
        <div className="relative z-10 flex flex-col justify-between h-full p-12 text-white">
          <div className="flex items-center gap-3">
            <ZLogo size={44} />
            <span className="font-display font-bold text-2xl tracking-tight">Zymerex Premi</span>
          </div>
          <div>
            <h1 className="font-display font-extrabold text-5xl leading-tight tracking-tight">
              Trasforma ogni <span className="text-[#7DD3FC]">vendita</span> in un premio.
            </h1>
            <p className="text-blue-100 mt-4 max-w-md">
              Gestionale promozioni e premi per agenti e capi area. Monitora soglie, sblocca premi e traccia i compensi in tempo reale.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <ZLogo size={40} />
            <span className="font-display font-bold text-xl text-[#0F1E33]">Zymerex Premi</span>
          </div>

          {step === "credentials" ? (
            <>
              <h2 className="font-display font-bold text-3xl tracking-tight text-[#0F1E33]">Accedi</h2>
              <p className="text-[#5A6B85] text-sm mt-1 mb-8">Inserisci le tue credenziali per continuare.</p>
              <form onSubmit={submit} className="space-y-4">
                {sessionMessage && (
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-2" data-testid="session-expired-message">
                    <Clock className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} /> <span>{sessionMessage}</span>
                  </p>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-[#33435E]">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="nome@azienda.it" className="bg-white border-[#DCE7F5] h-11 focus-visible:ring-[#0B6BCB]"
                    data-testid="login-email-input" required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-[#33435E]">Password</Label>
                  <div className="relative">
                    <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••" className="bg-white border-[#DCE7F5] h-11 pr-11 focus-visible:ring-[#0B6BCB]"
                      data-testid="login-password-input" required />
                    <button type="button" onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#5A6B85] hover:text-[#0B6BCB] transition-colors"
                      aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                      data-testid="toggle-password-visibility">
                      {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                    </button>
                  </div>
                </div>
                {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">{error}</p>}
                <Button type="submit" disabled={loading}
                  className="w-full h-11 bg-[#0B6BCB] hover:bg-[#0A5CB0] text-white font-semibold transition-colors"
                  data-testid="login-submit-button">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accedi"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-2 text-[#0B6BCB]">
                <ShieldCheck className="w-5 h-5" strokeWidth={1.5} />
                <span className="text-xs uppercase tracking-wide font-semibold">Verifica in due passaggi</span>
              </div>
              <h2 className="font-display font-bold text-3xl tracking-tight text-[#0F1E33]">Inserisci il codice</h2>
              <p className="text-[#5A6B85] text-sm mt-1 mb-8">
                Per la tua sicurezza, inserisci il codice a 6 cifre inviato a <strong className="text-[#33435E]">{email}</strong>.
              </p>
              <form onSubmit={submitOtp} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="otp" className="text-[#33435E]">Codice di verifica</Label>
                  <Input id="otp" inputMode="numeric" maxLength={6} value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                    className="bg-white border-[#DCE7F5] h-12 text-center text-2xl tracking-[0.4em] font-display focus-visible:ring-[#0B6BCB]"
                    data-testid="otp-code-input" required />
                </div>
                {info && <p className="text-sm text-[#0B6BCB] bg-[#EEF4FC] border border-[#DCE7F5] rounded-md px-3 py-2" data-testid="otp-info">{info}</p>}
                {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="otp-error">{error}</p>}
                <Button type="submit" disabled={loading || code.length < 6}
                  className="w-full h-11 bg-[#0B6BCB] hover:bg-[#0A5CB0] text-white font-semibold transition-colors"
                  data-testid="otp-verify-button">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verifica e accedi"}
                </Button>
              </form>
              <div className="flex items-center justify-between mt-5 text-sm">
                <button onClick={backToCredentials} className="flex items-center gap-1 text-[#5A6B85] hover:text-[#0F1E33] transition-colors" data-testid="otp-back-button">
                  <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Indietro
                </button>
                <button onClick={resend} className="text-[#0B6BCB] hover:underline" data-testid="otp-resend-button">
                  Reinvia codice
                </button>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
